import { Controller } from '@hotwired/stimulus';

export default class extends Controller {
  static targets = [
    'indicator',
    'radio',
    'range',
    'day',
    'blank',
    'apply',
    'link',
  ];

  DEFAULT_FILTER = { criterium: null, quality: '0' };
  HIGHLIGHT_CLASSES = ['!bg-gold', '!bg-opacity-50'];
  LOCAL_STORAGE_KEY = 'goodDaysFilter';

  connect() {
    this.dayTargets.forEach((day) =>
      this.tdFor(day).classList.add('transition'),
    );
    this.loadFilter();
    this.update();
  }

  update() {
    this.toggleIndicatorClasses();

    const countMatches = this.highlightMatchingDays();
    this.toggleBlankTarget(countMatches);
    this.applyTarget.disabled = this.isDefaultFilter;
    this.rangeTarget.disabled = this.isDefaultFilter;

    this.saveFilter();
    this.updatePdfLink();
  }

  updatePdfLink() {
    if (this.hasLinkTarget) {
      const url = new URL(this.linkTarget.dataset.href);
      const params = new URLSearchParams(url.search);

      params.set('good_days_criterium', this.selectedCriterium);
      params.set('good_days_quality', this.rangeTarget.value);

      let hash = document.location.hash.substring(1);
      if (hash) hash = `#${hash}`;

      // Write url to data-href, so it's accessible by `pdf-download` controller
      this.linkTarget.dataset.href =
        url.origin + url.pathname + '?' + params.toString() + hash;
    }
  }

  toggleIndicatorClasses() {
    if (!this.hasIndicatorTarget) return;

    const active = !this.isDefaultFilter;
    this.toggleClasses(
      this.indicatorTarget,
      ['text-gold', 'border-gold'],
      active,
    );
  }

  toggleClasses(element, classes, condition) {
    classes.forEach((cls) => element.classList.toggle(cls, condition));
  }

  highlightMatchingDays() {
    let countMatches = 0;
    const isActive = !!this.selectedCriterium;

    this.dayTargets.forEach((day) => {
      const td = this.tdFor(day);

      if (isActive && this.matches(day)) {
        td.classList.add(...this.HIGHLIGHT_CLASSES);
        countMatches++;
      } else td.classList.remove(...this.HIGHLIGHT_CLASSES);
    });

    return countMatches;
  }

  toggleBlankTarget(countMatches) {
    const hasFilter = !!this.selectedCriterium;
    this.blankTarget.classList.toggle('hidden', countMatches > 0 || !hasFilter);
  }

  /////

  matches(day) {
    return (
      (this.filterPerfect && this.isPerfect(day)) ||
      (this.filterGood && (this.isGood(day) || this.isPerfect(day))) ||
      (this.filterSuitable &&
        (this.isSuitable(day) || this.isGood(day) || this.isPerfect(day)))
    );
  }

  RATINGS =
    // Suitable / Good / Perfect
    [[0], [2, 1], [2, 1]];

  REGENTS = {
    thrive: [3, 5, 9],
    start: [1, 6, 11],
    release: [2, 7, 12],
    receive: [10],
    self_development: [4, 8, 11],
    any_kind: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12],
  };

  isSuitable(day) {
    return (
      this.RATINGS[0].includes(this.rating(day)) &&
      this.REGENTS[this.selectedCriterium].includes(this.regent(day)) &&
      !this.disturbance(day)
    );
  }

  isGood(day) {
    return (
      this.RATINGS[1].includes(this.rating(day)) &&
      this.REGENTS[this.selectedCriterium].includes(this.regent(day)) &&
      this.disturbance(day)
    );
  }

  isPerfect(day) {
    return (
      this.RATINGS[2].includes(this.rating(day)) &&
      this.REGENTS[this.selectedCriterium].includes(this.regent(day)) &&
      !this.disturbance(day)
    );
  }

  tdFor(day) {
    return day.closest('td');
  }

  disturbance(day) {
    return day.dataset.disturbance === 'true';
  }

  rating(day) {
    return parseInt(day.dataset.rating, 10);
  }

  regent(day) {
    return parseInt(day.dataset.regent, 10);
  }

  get filterSuitable() {
    return this.rangeTarget.value === '0';
  }

  get filterGood() {
    return this.rangeTarget.value === '1';
  }

  get filterPerfect() {
    return this.rangeTarget.value === '2';
  }

  get selectedCriterium() {
    const selectedCriterium = this.radioTargets.find((radio) => radio.checked);
    if (!selectedCriterium) return null;

    return selectedCriterium.value;
  }

  isCriterium(filterValue) {
    return this.selectedCriterium === filterValue;
  }

  saveFilter() {
    localStorage.setItem(
      this.LOCAL_STORAGE_KEY,
      JSON.stringify(this.currentFilter),
    );
  }

  resetFilter() {
    localStorage.removeItem(this.LOCAL_STORAGE_KEY);
    this.loadFilter();
    this.update();
  }

  loadFilter() {
    // On printing, there is no localStorage and the checkboxes are set by params
    if (this.onPrint) return;

    const loadedFilter =
      JSON.parse(localStorage.getItem(this.LOCAL_STORAGE_KEY)) ||
      this.DEFAULT_FILTER;

    this.radioTargets.forEach((radio) => {
      radio.checked = loadedFilter.criterium === radio.value;
    });

    this.rangeTarget.value = loadedFilter.quality;
  }

  get currentFilter() {
    return {
      criterium: this.selectedCriterium,
      quality: this.rangeTarget.value,
    };
  }

  get isDefaultFilter() {
    return (
      JSON.stringify(this.currentFilter) === JSON.stringify(this.DEFAULT_FILTER)
    );
  }

  get onPrint() {
    return document
      .getElementsByTagName('body')[0]
      .classList.contains('js-print');
  }
}
